import * as React from 'react';
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ui.css';
import { Panel } from './Panel';
import { Text } from './Text';

/*
 * Modal — a fixed overlay + Panel. DOM/CSS replacement for MUI <Dialog>.
 * Portals to <body> so the Stage transform never scales it.
 *
 * Behaviour expected of a dialog: it fades and lifts in, Escape closes it, focus
 * moves inside and Tab is trapped there, the page behind it can't scroll, and
 * the title names the dialog for screen readers. Closing is immediate — an exit
 * animation on a portalled overlay reads as a flash, not a fade.
 */

interface ModalProps {
  open: boolean;
  onClose?: () => void;
  title?: React.ReactNode;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  /** Clicking the backdrop closes the modal. Default true when onClose exists. */
  dismissOnBackdrop?: boolean;
  /** Override the panel's max width (default 420px, from ui.css). */
  width?: number | string;
}

// Modals can overlap (one closing while the next opens), so the body lock is
// counted rather than saved/restored per modal — the last one out unlocks.
let openModals = 0;

const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  actions,
  dismissOnBackdrop,
  width,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Escape to close, and Tab cycles within the dialog rather than escaping to
  // the page behind it.
  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock the page behind the modal so it can't scroll under the overlay.
  useEffect(() => {
    if (!open) return;
    openModals += 1;
    document.body.style.overflow = 'hidden';
    return () => {
      openModals -= 1;
      if (openModals === 0) document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const backdropCloses = dismissOnBackdrop ?? !!onClose;

  return createPortal(
    <div className="ui-modal-overlay" onClick={backdropCloses ? onClose : undefined}>
      <Panel
        variant="solid"
        className="ui-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        ref={panelRef}
        style={width !== undefined ? { maxWidth: width } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <Text as="h2" id={titleId} variant="heading" style={{ marginBottom: 'var(--space-4)' }}>
            {title}
          </Text>
        )}
        {children}
        {actions && (
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-3)',
              justifyContent: 'flex-end',
              marginTop: 'var(--space-5)',
            }}
          >
            {actions}
          </div>
        )}
      </Panel>
    </div>,
    document.body,
  );
};

export default Modal;
